import React, { useDeferredValue, useEffect, useMemo, useState, useTransition } from "react";
import { Link } from "react-router-dom";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { adminApi } from "@/lib/adminApi";
import { entityConfigs } from "./entityConfigs";

function formatDate(value) {
    if (!value) return "—";
    try {
        return new Intl.DateTimeFormat(undefined, {
            month: "short",
            day: "numeric",
            year: "numeric",
        }).format(new Date(value));
    } catch {
        return value;
    }
}

function statusBadgeClass(value) {
    const normalized = String(value || "").toLowerCase();
    if (["published", "active", "upcoming", "live", "featured"].includes(normalized)) {
        return "border-emerald-500/30 bg-emerald-500/10 text-emerald-200";
    }
    if (["draft", "inactive", "delayed"].includes(normalized)) {
        return "border-amber-500/30 bg-amber-500/10 text-amber-200";
    }
    if (["hidden", "retired", "cancelled", "historical", "legendary", "legend"].includes(normalized)) {
        return "border-zinc-500/30 bg-zinc-500/10 text-zinc-300";
    }
    return "border-sky-500/30 bg-sky-500/10 text-sky-200";
}

export function AdminStatusBadge({ value }) {
    return (
        <Badge variant="outline" className={statusBadgeClass(value)}>
            {String(value || "—").replaceAll("-", " ")}
        </Badge>
    );
}

function renderCell(column, item) {
    const value = item[column.key];
    if (column.type === "badge") {
        return <AdminStatusBadge value={value} />;
    }
    if (column.type === "boolean") {
        return value ? "Yes" : "No";
    }
    if (column.type === "date") {
        return formatDate(value);
    }
    return value || "—";
}

function makeSessionRow() {
    const id = `session-${Date.now()}-${Math.round(Math.random() * 10000)}`;
    return {
        id,
        session_type: "FP1",
        session_name: "",
        start_time_utc: "",
        end_time_utc: "",
        status: "upcoming",
    };
}

function SessionEditor({ value, onChange, config }) {
    const sessions = value || [];
    const sessionTypes = config.meta?.sessionTypes || [];
    const sessionStatuses = config.meta?.sessionStatuses || [];

    const updateRow = (id, field, nextValue) => {
        onChange(
            sessions.map((session) =>
                session.id === id ? { ...session, [field]: nextValue } : session
            )
        );
    };

    const removeRow = (id) => onChange(sessions.filter((session) => session.id !== id));

    return (
        <div className="flex flex-col gap-3 rounded-md border border-white/10 bg-black/20 p-3">
            {sessions.length ? (
                sessions.map((session) => (
                    <div key={session.id} className="flex flex-col gap-3 rounded-md border border-white/10 bg-white/[0.03] p-3">
                        <div className="grid gap-3 md:grid-cols-2">
                            <div className="flex flex-col gap-2">
                                <label className="text-xs text-zinc-400">Session Type</label>
                                <Select value={session.session_type} onValueChange={(next) => updateRow(session.id, "session_type", next)}>
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select type" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectGroup>
                                            {sessionTypes.map((option) => (
                                                <SelectItem key={option} value={option}>
                                                    {option}
                                                </SelectItem>
                                            ))}
                                        </SelectGroup>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="flex flex-col gap-2">
                                <label className="text-xs text-zinc-400">Status</label>
                                <Select value={session.status} onValueChange={(next) => updateRow(session.id, "status", next)}>
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select status" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectGroup>
                                            {sessionStatuses.map((option) => (
                                                <SelectItem key={option} value={option}>
                                                    {option}
                                                </SelectItem>
                                            ))}
                                        </SelectGroup>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="flex flex-col gap-2 md:col-span-2">
                                <label className="text-xs text-zinc-400">Session Name</label>
                                <Input value={session.session_name} onChange={(event) => updateRow(session.id, "session_name", event.target.value)} />
                            </div>
                            <div className="flex flex-col gap-2">
                                <label className="text-xs text-zinc-400">Start Time (UTC)</label>
                                <Input type="datetime-local" value={session.start_time_utc} onChange={(event) => updateRow(session.id, "start_time_utc", event.target.value)} />
                            </div>
                            <div className="flex flex-col gap-2">
                                <label className="text-xs text-zinc-400">End Time (UTC)</label>
                                <Input type="datetime-local" value={session.end_time_utc || ""} onChange={(event) => updateRow(session.id, "end_time_utc", event.target.value)} />
                            </div>
                        </div>
                        <div className="flex justify-end">
                            <Button type="button" variant="outline" size="sm" onClick={() => removeRow(session.id)}>
                                Remove session
                            </Button>
                        </div>
                    </div>
                ))
            ) : (
                <div className="rounded-md border border-dashed border-white/10 px-3 py-4 text-sm text-zinc-400">
                    No sessions added yet.
                </div>
            )}
            <div>
                <Button type="button" variant="secondary" size="sm" onClick={() => onChange([...sessions, makeSessionRow()])}>
                    Add session
                </Button>
            </div>
        </div>
    );
}

export function AdminEntityDialog({ open, onOpenChange, config, item, onSaved }) {
    const [form, setForm] = useState(config.toFormValues(item));
    const [saving, setSaving] = useState(false);
    const isEditing = Boolean(item?.id);

    useEffect(() => {
        setForm(config.toFormValues(item));
    }, [config, item]);

    const setField = (name, value) => setForm((current) => ({ ...current, [name]: value }));

    const handleSubmit = async (event) => {
        event.preventDefault();
        setSaving(true);
        try {
            const payload = config.toApiPayload(form);
            if (isEditing) {
                await config.api.update(item.id, payload);
            } else {
                await config.api.create(payload);
            }
            onOpenChange(false);
            onSaved?.();
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto border-white/10 bg-[#11151b] text-white">
                <DialogHeader>
                    <DialogTitle>{isEditing ? `Edit ${config.singular}` : `New ${config.singular}`}</DialogTitle>
                    <DialogDescription className="text-zinc-400">
                        {config.description}
                    </DialogDescription>
                </DialogHeader>
                <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
                    <div className="grid gap-4 md:grid-cols-2">
                        {config.fields.map((field) => {
                            const value = form[field.name];
                            const isWide = field.type === "textarea" || field.type === "sessions";
                            return (
                                <div key={field.name} className={`flex flex-col gap-2 ${isWide ? "md:col-span-2" : ""}`}>
                                    <label className="text-sm font-medium text-zinc-100">{field.label}</label>
                                    {field.type === "text" || field.type === "number" || field.type === "datetime" ? (
                                        <Input
                                            type={field.type === "datetime" ? "datetime-local" : field.type}
                                            value={value ?? ""}
                                            onChange={(event) => setField(field.name, event.target.value)}
                                            required={field.required}
                                        />
                                    ) : null}
                                    {field.type === "textarea" ? (
                                        <Textarea
                                            value={value ?? ""}
                                            onChange={(event) => setField(field.name, event.target.value)}
                                            rows={field.name === "body" ? 8 : 4}
                                            required={field.required}
                                        />
                                    ) : null}
                                    {field.type === "select" ? (
                                        <Select value={String(value ?? "")} onValueChange={(next) => setField(field.name, next)}>
                                            <SelectTrigger>
                                                <SelectValue placeholder={`Select ${field.label.toLowerCase()}`} />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectGroup>
                                                    {field.options.map((option) => (
                                                        <SelectItem key={option} value={option}>
                                                            {option}
                                                        </SelectItem>
                                                    ))}
                                                </SelectGroup>
                                            </SelectContent>
                                        </Select>
                                    ) : null}
                                    {field.type === "checkbox" ? (
                                        <label className="flex items-center gap-3 rounded-md border border-white/10 bg-white/[0.03] px-3 py-2 text-sm">
                                            <input
                                                type="checkbox"
                                                checked={Boolean(value)}
                                                onChange={(event) => setField(field.name, event.target.checked)}
                                            />
                                            <span>{field.label}</span>
                                        </label>
                                    ) : null}
                                    {field.type === "array" ? (
                                        <Input
                                            value={value ?? ""}
                                            onChange={(event) => setField(field.name, event.target.value)}
                                            placeholder={field.help}
                                        />
                                    ) : null}
                                    {field.type === "sessions" ? (
                                        <SessionEditor
                                            value={value}
                                            onChange={(next) => setField(field.name, next)}
                                            config={config}
                                        />
                                    ) : null}
                                    {field.help ? <p className="text-xs text-zinc-500">{field.help}</p> : null}
                                </div>
                            );
                        })}
                    </div>
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                            Cancel
                        </Button>
                        <Button type="submit" disabled={saving}>
                            {saving ? "Saving..." : isEditing ? `Save ${config.singular}` : `Create ${config.singular}`}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

export function AdminDeleteDialog({ open, onOpenChange, title, description, onConfirm, busy }) {
    return (
        <AlertDialog open={open} onOpenChange={onOpenChange}>
            <AlertDialogContent className="border-white/10 bg-[#11151b] text-white">
                <AlertDialogHeader>
                    <AlertDialogTitle>{title}</AlertDialogTitle>
                    <AlertDialogDescription className="text-zinc-400">
                        {description}
                    </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={onConfirm} disabled={busy}>
                        {busy ? "Deleting..." : "Delete"}
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
}

export function AdminCollectionPage({ entityKey }) {
    const config = entityConfigs[entityKey];
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [query, setQuery] = useState("");
    const [filters, setFilters] = useState({});
    const [editorOpen, setEditorOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleteBusy, setDeleteBusy] = useState(false);
    const [isPending, startTransition] = useTransition();
    const deferredQuery = useDeferredValue(query);

    const loadItems = async () => {
        setLoading(true);
        setError("");
        try {
            const params = { q: deferredQuery || undefined, ...filters };
            const data = await config.api.list(params);
            startTransition(() => setItems(data));
        } catch (fetchError) {
            setError(fetchError?.response?.data?.detail || "Unable to load admin content.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadItems();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deferredQuery, JSON.stringify(filters), entityKey]);

    const openCreate = () => {
        setSelectedItem(null);
        setEditorOpen(true);
    };

    const openEdit = (item) => {
        setSelectedItem(item);
        setEditorOpen(true);
    };

    const runDelete = async () => {
        if (!deleteTarget) return;
        setDeleteBusy(true);
        try {
            await config.api.remove(deleteTarget.id);
            setDeleteTarget(null);
            await loadItems();
        } finally {
            setDeleteBusy(false);
        }
    };

    const renderRowActions = (item) => {
        if (entityKey !== "news") return null;
        return (
            <div className="flex flex-wrap gap-2">
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={async () => {
                        await adminApi.news.publish(
                            item.id,
                            item.status === "published" ? "draft" : "published"
                        );
                        await loadItems();
                    }}
                >
                    {item.status === "published" ? "Unpublish" : "Publish"}
                </Button>
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={async () => {
                        await adminApi.news.feature(item.id, !item.featured);
                        await loadItems();
                    }}
                >
                    {item.featured ? "Unfeature" : "Feature"}
                </Button>
            </div>
        );
    };

    return (
        <div className="flex flex-col gap-6">
            <div className="rounded-2xl border border-white/10 bg-[#11151b] p-6">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                    <div className="flex flex-col gap-2">
                        <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">
                            Admin Collection
                        </span>
                        <h1 className="font-display text-4xl tracking-tight">{config.label}</h1>
                        <p className="max-w-2xl text-sm text-zinc-400">{config.description}</p>
                    </div>
                    <div className="flex flex-wrap gap-3">
                        {entityKey === "calendar" ? (
                            <Button type="button" variant="outline" onClick={() => adminApi.calendar.refresh()}>
                                Refresh Placeholder
                            </Button>
                        ) : null}
                        <Button type="button" onClick={openCreate}>
                            Add {config.singular}
                        </Button>
                    </div>
                </div>
                <div className="mt-6 flex flex-col gap-4 lg:flex-row">
                    <div className="flex-1">
                        <Input
                            value={query}
                            onChange={(event) => setQuery(event.target.value)}
                            placeholder={`Search ${config.label.toLowerCase()}...`}
                        />
                    </div>
                    <div className="flex flex-wrap gap-3">
                        {config.listFilters.map((filter) => (
                            <div key={filter.name} className="min-w-[180px]">
                                <Select
                                    value={filters[filter.name] || "all"}
                                    onValueChange={(next) =>
                                        setFilters((current) => ({
                                            ...current,
                                            [filter.name]: next === "all" ? undefined : next,
                                        }))
                                    }
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder={filter.label} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectGroup>
                                            <SelectItem value="all">All {filter.label}</SelectItem>
                                            {filter.options.map((option) => (
                                                <SelectItem key={option} value={option}>
                                                    {option}
                                                </SelectItem>
                                            ))}
                                        </SelectGroup>
                                    </SelectContent>
                                </Select>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <div className="rounded-2xl border border-white/10 bg-[#11151b] p-4">
                {loading ? (
                    <div className="rounded-xl border border-white/10 px-4 py-10 text-center text-zinc-400">
                        Loading {config.label.toLowerCase()}...
                    </div>
                ) : error ? (
                    <div className="rounded-xl border border-red-500/20 bg-red-500/10 px-4 py-10 text-center text-red-100">
                        {error}
                    </div>
                ) : items.length ? (
                    <Table>
                        <TableHeader>
                            <TableRow>
                                {config.columns.map((column) => (
                                    <TableHead key={column.key}>{column.label}</TableHead>
                                ))}
                                <TableHead>Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {items.map((item) => (
                                <TableRow key={item.id}>
                                    {config.columns.map((column) => (
                                        <TableCell key={column.key}>{renderCell(column, item)}</TableCell>
                                    ))}
                                    <TableCell>
                                        <div className="flex flex-wrap gap-2">
                                            {renderRowActions(item)}
                                            <Button type="button" variant="outline" size="sm" onClick={() => openEdit(item)}>
                                                Edit
                                            </Button>
                                            <Button type="button" variant="outline" size="sm" onClick={() => setDeleteTarget(item)}>
                                                Delete
                                            </Button>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                ) : (
                    <div className="rounded-xl border border-dashed border-white/10 px-4 py-10 text-center text-zinc-400">
                        No {config.label.toLowerCase()} matched the current filters.
                    </div>
                )}
                {isPending ? <div className="mt-3 text-xs text-zinc-500">Updating table…</div> : null}
            </div>

            <AdminEntityDialog
                open={editorOpen}
                onOpenChange={setEditorOpen}
                config={config}
                item={selectedItem}
                onSaved={loadItems}
            />
            <AdminDeleteDialog
                open={Boolean(deleteTarget)}
                onOpenChange={(open) => {
                    if (!open) setDeleteTarget(null);
                }}
                title={`Delete ${config.singular}`}
                description={`This will permanently remove ${deleteTarget?.title || deleteTarget?.display_name || deleteTarget?.name || deleteTarget?.race_name || deleteTarget?.term || "this item"}.`}
                onConfirm={runDelete}
                busy={deleteBusy}
            />
        </div>
    );
}

export function AdminFeaturedManager() {
    const config = {
        key: "featured",
        label: "Featured Content",
        singular: "Featured Item",
        description: "Promote content blocks for homepage or editorial modules.",
        api: adminApi.featured,
        columns: [
            { key: "type", label: "Type", type: "badge" },
            { key: "reference_id", label: "Reference ID" },
            { key: "sort_order", label: "Order" },
            { key: "active", label: "Active", type: "boolean" },
        ],
        listFilters: [],
        fields: [
            { name: "type", label: "Type", type: "select", options: ["news", "driver", "team", "race", "stat"], required: true },
            { name: "reference_id", label: "Reference ID", type: "text", required: true },
            { name: "title_override", label: "Title Override", type: "text" },
            { name: "description_override", label: "Description Override", type: "textarea" },
            { name: "sort_order", label: "Sort Order", type: "number" },
            { name: "active", label: "Active", type: "checkbox" },
        ],
        defaults: {
            type: "news",
            reference_id: "",
            title_override: "",
            description_override: "",
            sort_order: 0,
            active: true,
        },
        toFormValues(item) {
            return { ...this.defaults, ...item };
        },
        toApiPayload(form) {
            return {
                type: form.type,
                reference_id: form.reference_id,
                title_override: form.title_override,
                description_override: form.description_override,
                sort_order: Number(form.sort_order || 0),
                active: Boolean(form.active),
            };
        },
    };

    return <AdminCollectionPageInternal config={config} />;
}

function AdminCollectionPageInternal({ config }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editorOpen, setEditorOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);

    const loadItems = async () => {
        setLoading(true);
        try {
            const data = await config.api.list();
            setItems(data);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadItems();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="flex flex-col gap-4 rounded-2xl border border-white/10 bg-[#11151b] p-4">
            <div className="flex items-end justify-between gap-3">
                <div className="flex flex-col gap-1">
                    <h2 className="font-display text-3xl tracking-tight">{config.label}</h2>
                    <p className="text-sm text-zinc-400">{config.description}</p>
                </div>
                <Button type="button" onClick={() => { setSelectedItem(null); setEditorOpen(true); }}>
                    Add Featured Item
                </Button>
            </div>
            {loading ? (
                <div className="rounded-xl border border-white/10 px-4 py-8 text-center text-zinc-400">Loading featured items…</div>
            ) : (
                <Table>
                    <TableHeader>
                        <TableRow>
                            {config.columns.map((column) => (
                                <TableHead key={column.key}>{column.label}</TableHead>
                            ))}
                            <TableHead>Actions</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {items.map((item) => (
                            <TableRow key={item.id}>
                                {config.columns.map((column) => (
                                    <TableCell key={column.key}>{renderCell(column, item)}</TableCell>
                                ))}
                                <TableCell>
                                    <div className="flex gap-2">
                                        <Button type="button" variant="outline" size="sm" onClick={() => { setSelectedItem(item); setEditorOpen(true); }}>
                                            Edit
                                        </Button>
                                        <Button type="button" variant="outline" size="sm" onClick={() => setDeleteTarget(item)}>
                                            Delete
                                        </Button>
                                    </div>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}
            <AdminEntityDialog
                open={editorOpen}
                onOpenChange={setEditorOpen}
                config={config}
                item={selectedItem}
                onSaved={loadItems}
            />
            <AdminDeleteDialog
                open={Boolean(deleteTarget)}
                onOpenChange={(open) => !open && setDeleteTarget(null)}
                title="Delete featured item"
                description={`This will remove featured item ${deleteTarget?.reference_id || ""}.`}
                onConfirm={async () => {
                    if (deleteTarget) {
                        await config.api.remove(deleteTarget.id);
                        setDeleteTarget(null);
                        await loadItems();
                    }
                }}
                busy={false}
            />
        </div>
    );
}

export function AdminDashboardSummary() {
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        adminApi.summary()
            .then(setSummary)
            .finally(() => setLoading(false));
    }, []);

    const totals = summary?.totals || {};
    const database = summary?.database || {};
    const providers = summary?.providers || [];

    return (
        <div className="flex flex-col gap-6">
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                {[
                    ["Total News", totals.news_articles],
                    ["Published", totals.published_articles],
                    ["Drafts", totals.draft_articles],
                    ["Active Drivers", totals.active_drivers],
                    ["Active Teams", totals.active_teams],
                    ["Upcoming Races", totals.upcoming_races],
                    ["Records", totals.records_count],
                    ["Glossary Terms", totals.glossary_terms_count],
                ].map(([label, value]) => (
                    <div key={label} className="rounded-2xl border border-white/10 bg-[#11151b] p-5">
                        <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">{label}</p>
                        <p className="mt-3 font-display text-4xl tracking-tight">{loading ? "…" : value ?? 0}</p>
                    </div>
                ))}
            </div>
            <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_320px]">
                <div className="rounded-2xl border border-white/10 bg-[#11151b] p-5">
                    <div className="flex items-center justify-between gap-3">
                        <div>
                            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">Recently Updated</p>
                            <h2 className="mt-2 font-display text-3xl tracking-tight">Fresh changes across content</h2>
                        </div>
                    </div>
                    <div className="mt-5 flex flex-col gap-3">
                        {(summary?.recent_updates || []).map((item) => (
                            <div key={`${item.collection}-${item.id}`} className="flex items-center justify-between gap-3 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3">
                                <div className="min-w-0">
                                    <p className="truncate text-sm font-semibold text-white">{item.title}</p>
                                    <p className="mt-1 text-xs uppercase tracking-[0.18em] text-zinc-500">
                                        {item.collection}
                                    </p>
                                </div>
                                <div className="text-right">
                                    <AdminStatusBadge value={item.status || "updated"} />
                                    <p className="mt-2 text-xs text-zinc-500">{formatDate(item.updated_at)}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
                <div className="rounded-2xl border border-white/10 bg-[#11151b] p-5">
                    <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">Quick Actions</p>
                    <h2 className="mt-2 font-display text-3xl tracking-tight">Jump straight into edits</h2>
                    <div className="mt-5 flex flex-col gap-3">
                        {(summary?.quick_actions || []).map((action) => (
                            <Link
                                key={action.route}
                                to={action.route}
                                className="rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm font-medium text-zinc-100 transition hover:border-white/20 hover:bg-white/[0.05]"
                            >
                                {action.label}
                            </Link>
                        ))}
                    </div>
                </div>
            </div>
            <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
                <div className="rounded-2xl border border-white/10 bg-[#11151b] p-5">
                    <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">Database Status</p>
                    <div className="mt-3 flex items-center justify-between gap-3">
                        <div>
                            <p className="font-display text-3xl tracking-tight">{database.connected ? "Connected" : "Fallback"}</p>
                            <p className="mt-2 text-sm text-zinc-400">{database.database || "outlapped"}</p>
                        </div>
                        <AdminStatusBadge value={database.status || "offline"} />
                    </div>
                    {database.detail ? <p className="mt-4 text-sm text-zinc-500">{database.detail}</p> : null}
                </div>
                <div className="rounded-2xl border border-white/10 bg-[#11151b] p-5">
                    <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">Provider Status</p>
                    <div className="mt-4 grid gap-3">
                        {providers.map((provider) => (
                            <div key={provider.provider_name} className="flex items-center justify-between gap-3 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3">
                                <div>
                                    <p className="font-semibold text-white">{provider.provider_name}</p>
                                    <p className="text-xs text-zinc-500">{provider.api_key_status}</p>
                                </div>
                                <AdminStatusBadge value={provider.enabled ? "active" : "inactive"} />
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}
